
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  X,
  Star,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import { useBrandingStore } from '../../stores/branding.store';
import { theme } from '../../lib/theme';
import { getStoredAvatar } from '../../lib/avatar';
import { visibleNavSections, percorsiDiVoce } from '../../lib/navConfig';
import { usePermessiStore } from '../../stores/permessi.store';
import { useFavorites } from '../../hooks/useFavorites';
import { iconName } from '../../lib/iconUtils';
import VersionFooter from './VersionFooter';

export default function Sidebar({ collapsed, onCollapse, onCloseMobile }) {
  const { user, logout } = useAuthStore();
  const branding = useBrandingStore((s) => s.branding);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ruolo = user?.ruolo || 'OPERATOR';
  const [avatarUrl, setAvatarUrl] = useState(null);
  // Track which section accordions the user has EXPANDED. Empty by default →
  // every section starts collapsed at login (for every role), and sections
  // that appear only after the user object loads also start collapsed. The
  // user can expand any section; the choice lives for the session only.
  const [expandedSections, setExpandedSections] = useState(new Set());
  // Sections + items filtered to what this user can actually open. Empty
  // sections are dropped so a MANAGER never sees an IAM header with nothing
  // under it (fpace bug).
  const permessi = usePermessiStore((s) => s.permessi);
  const catalogo = usePermessiStore((s) => s.catalogo);
  const statoPermessi = usePermessiStore((s) => s.stato);
  const sections = visibleNavSections(user, statoPermessi === 'pronto' ? { permessi, catalogo } : null);
  // Per-item star toggle. Starred items show up on the HomeDashboard's
  // "Preferiti" row.
  const { isFavorite, toggle: toggleFavorite } = useFavorites();

  // Load avatar from localStorage and listen for changes
  useEffect(() => {
    const loadAvatar = () => {
      if (user?.id) {
        setAvatarUrl(getStoredAvatar(user.id));
      }
    };
    loadAvatar();
    window.addEventListener('wfm-avatar-changed', loadAvatar);
    return () => window.removeEventListener('wfm-avatar-changed', loadAvatar);
  }, [user?.id]);

  const handleLogout = async () => {
    onCloseMobile?.();
    await logout();
    navigate('/login');
  };

  const toggleSection = (label) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const linkClasses = ({ isActive }) => {
    const activeStyle = isActive
      ? 'bg-[var(--wfm-sidebar-active-bg)] text-[var(--wfm-sidebar-active-text)] shadow-[inset_3px_0_0_0_var(--wfm-accent)]'
      : 'text-[var(--wfm-sidebar-text)] hover:bg-[var(--wfm-sidebar-hover-bg)] hover:text-[var(--wfm-sidebar-heading)]';
    if (collapsed) {
      return `flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors ${activeStyle}`;
    }
    return `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium leading-snug transition-colors ${activeStyle}`;
  };

  return (
    <div
      className="flex flex-col h-full"
      style={{
        backgroundColor: 'var(--wfm-sidebar-bg)',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        ...(collapsed ? {} : { paddingLeft: 'env(safe-area-inset-left, 0px)' }),
      }}
    >
      {/* Header — h-16 (64px) aligns pixel-for-pixel with TopBar's bottom border (both offset by safe-area-inset-top) */}
      {!collapsed ? (
        <div
          className="flex items-center justify-between gap-2.5 h-16 px-4 border-b shrink-0"
          style={{ borderColor: 'var(--wfm-sidebar-border)' }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-8 h-8 rounded-lg object-contain shrink-0" />
            ) : (
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-sm font-extrabold"
                style={{ backgroundColor: 'var(--wfm-sidebar-active-bg)', color: 'var(--wfm-sidebar-heading)' }}
              >
                {branding.logo_icon_letter || 'N'}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-[15px] font-bold leading-tight tracking-tight truncate" style={{ color: 'var(--wfm-sidebar-heading)' }}>
                {branding.company_name || 'NexAdvantage'}
              </h1>
              <p className="text-[11px] font-medium leading-tight truncate mt-0.5" style={{ color: 'var(--wfm-sidebar-subtitle)' }}>
                {branding.company_subtitle || 'WorkFlow Manager'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Chiudi menu"
            className="w-11 h-11 -mr-1.5 rounded-lg flex items-center justify-center shrink-0 text-[var(--wfm-sidebar-subtitle)] hover:text-[var(--wfm-sidebar-heading)] hover:bg-[var(--wfm-sidebar-hover-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 transition-colors lg:hidden"
          >
            <X size={18} />
          </button>
          <button
            type="button"
            onClick={onCollapse}
            title="Comprimi menu"
            aria-label="Comprimi menu"
            className="hidden lg:flex w-8 h-8 rounded-lg items-center justify-center shrink-0 text-[var(--wfm-sidebar-subtitle)] hover:text-[var(--wfm-sidebar-heading)] hover:bg-[var(--wfm-sidebar-hover-bg)] transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
        </div>
      ) : (
        <div
          className="flex items-center justify-center h-16 px-3 border-b shrink-0"
          style={{ borderColor: 'var(--wfm-sidebar-border)' }}
        >
          <button
            type="button"
            onClick={onCollapse}
            title="Espandi menu"
            aria-label="Espandi menu"
            className="w-10 h-10 mx-auto rounded-lg flex items-center justify-center gap-0.5 bg-[var(--wfm-sidebar-hover-bg)] hover:bg-[var(--wfm-sidebar-active-bg)] transition-colors"
            style={{ color: 'var(--wfm-sidebar-heading)' }}
          >
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-5 h-5 object-contain shrink-0" />
            ) : (
              <span className="text-sm font-extrabold leading-none">
                {branding.logo_icon_letter || 'N'}
              </span>
            )}
            <ChevronRight size={14} className="shrink-0 text-[var(--wfm-sidebar-subtitle)]" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav
        className={
          collapsed
            ? 'wfm-sidebar-nav-collapsed flex-1 overflow-y-auto overscroll-contain px-3 py-3'
            : 'wfm-sidebar-nav flex-1 overflow-y-auto overscroll-contain px-3 py-3 space-y-2'
        }
      >
        {sections.map((section, sectionIdx) => {
          const isCollapsed = !expandedSections.has(section.label);
          // Count distinct, in-order group labels among VISIBLE items so the
          // accordion's animated maxHeight accounts for the subgroup headers
          // (one per group, ~28 px). Items without `group` add nothing here.
          let groupCount = 0;
          let prev = null;
          for (const it of section.items) {
            if (it.group && it.group !== prev) { groupCount += 1; prev = it.group; }
            else if (!it.group) { prev = null; }
          }
          const ITEM_H = 48;
          const HEADER_H = 30;
          let runGroup = null; // resets per section, drives subgroup rendering
          return (
            <div key={section.label}>
              {!collapsed ? (
                <button
                  type="button"
                  onClick={() => toggleSection(section.label)}
                  className="flex items-center justify-between w-full px-3 mb-1 py-1.5 rounded-md hover:bg-[var(--wfm-sidebar-hover-bg)] transition-colors group"
                >
                  <span className="text-[11px] uppercase tracking-wider text-[var(--wfm-sidebar-section)] font-semibold group-hover:text-[var(--wfm-sidebar-heading)] transition-colors">
                    {section.label}
                  </span>
                  {isCollapsed ? (
                    <ChevronRight size={14} className="shrink-0 text-[var(--wfm-sidebar-section)] group-hover:text-[var(--wfm-sidebar-heading)] transition-colors" />
                  ) : (
                    <ChevronDown size={14} className="shrink-0 text-[var(--wfm-sidebar-section)] group-hover:text-[var(--wfm-sidebar-heading)] transition-colors" />
                  )}
                </button>
              ) : (
                sectionIdx > 0 && (
                  <div className="h-px w-6 mx-auto my-2" style={{ backgroundColor: 'var(--wfm-sidebar-border)' }} />
                )
              )}
              <div
                className="overflow-hidden transition-all duration-200 ease-in-out"
                style={{
                  maxHeight: isCollapsed && !collapsed
                    ? '0px'
                    : `${section.items.length * ITEM_H + groupCount * HEADER_H}px`,
                  opacity: isCollapsed && !collapsed ? 0 : 1,
                }}
              >
                <ul className={collapsed ? 'space-y-1.5' : 'space-y-1'}>
                  {section.items.map((item) => {
                    const starred = isFavorite(item.to);
                    // Insert a subgroup header whenever this item's `group`
                    // differs from the running value (one header per run).
                    // Collapsed-sidebar mode renders icons only — skip headers
                    // there so the rail stays tidy.
                    const showHeader = !collapsed && !!item.group && item.group !== runGroup;
                    if (item.group) runGroup = item.group;
                    else runGroup = null;
                    return (
                      <React.Fragment key={item.to}>
                        {showHeader && (
                          <li aria-hidden="true" className="px-3 pt-2.5 pb-1 mt-1 first:mt-0">
                            <span className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--wfm-sidebar-section)' }}>
                              {item.group}
                            </span>
                          </li>
                        )}
                        <li className="relative group">
                          {/* Una voce raggruppata resta accesa su tutte le sue
                              schede: altrimenti aprendo «Ferie» il menu
                              sembrerebbe non essere da nessuna parte. */}
                          <NavLink
                            to={item.to}
                            onClick={() => onCloseMobile?.()}
                            className={({ isActive }) => linkClasses({
                              isActive: isActive || percorsiDiVoce(item).some((p) => pathname === p || pathname.startsWith(`${p}/`)),
                            })}
                            title={item.label}
                          >
                            <item.icon size={collapsed ? 20 : 18} className="shrink-0" />
                            {!collapsed && <span className="flex-1 truncate pr-6">{item.label}</span>}
                          </NavLink>
                          {!collapsed && (
                            // Inline star button — kept OUTSIDE the NavLink so
                            // we don't nest interactive elements (invalid HTML).
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFavorite({ to: item.to, label: item.label, icon: iconName(item.icon) });
                              }}
                              title={starred ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                              aria-label={starred ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                              aria-pressed={starred}
                              className={`w-6 h-6 flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 rounded-md transition-opacity hover:bg-[var(--wfm-sidebar-hover-bg)] ${starred ? 'opacity-100 text-[var(--wfm-accent)]' : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-[var(--wfm-sidebar-subtitle)]'}`}
                            >
                              <Star size={14} fill={starred ? 'currentColor' : 'none'} />
                            </button>
                          )}
                        </li>
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="border-t px-3 py-3 shrink-0" style={{ borderColor: 'var(--wfm-sidebar-border)' }}>
        {user && (
          /* Il riquadro con nome e ruolo ora porta al profilo: è lì che sono
             finite le scelte di aspetto, e questo era il posto dove le si
             cercava. Il collegamento si aggiunge, niente viene spostato. */
          <button
            type="button"
            onClick={() => { navigate('/profilo'); onCloseMobile?.(); }}
            title="Vai al profilo — aspetto, password e sicurezza"
            aria-label="Vai al profilo"
            className={
              collapsed
                ? 'w-10 h-10 mx-auto mb-1.5 rounded-lg flex items-center justify-center hover:bg-[var(--wfm-sidebar-hover-bg)] transition-colors'
                : 'flex items-center gap-3 px-2.5 py-2 mb-1.5 w-full text-left rounded-lg hover:bg-[var(--wfm-sidebar-hover-bg)] transition-colors'
            }
          >
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white text-xs font-bold overflow-hidden shrink-0">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <>{user.nome?.[0]}{user.cognome?.[0]}</>
              )}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold leading-tight truncate" style={{ color: 'var(--wfm-sidebar-heading)' }}>
                  {user.nome} {user.cognome}
                </p>
                <p className="text-[11px] font-medium leading-tight truncate mt-0.5" style={{ color: 'var(--wfm-sidebar-subtitle)' }}>
                  {theme.roleLabels[ruolo] || ruolo}
                </p>
              </div>
            )}
          </button>
        )}
        {/* Chiaro/scuro e la scelta del tema stavano qui, in fondo al menu.
            Sono in Profilo → Aspetto: sono preferenze della persona, non
            navigazione, e lì si vedono in anteprima prima di scegliere —
            mentre qui la fila dei temi spariva appena il menu si riduceva.
            L'accesso è l'avatar in alto a destra, che porta al profilo. */}
        <button
          type="button"
          onClick={handleLogout}
          className={
            collapsed
              ? 'w-10 h-10 mx-auto rounded-lg flex items-center justify-center text-[var(--wfm-sidebar-text)] hover:bg-accent/20 hover:text-accent transition-colors'
              : 'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-[13px] font-medium text-[var(--wfm-sidebar-text)] hover:bg-accent/20 hover:text-accent transition-colors'
          }
          title="Esci"
          aria-label="Esci"
        >
          <LogOut size={collapsed ? 20 : 18} className="shrink-0" />
          {!collapsed && <span>Esci</span>}
        </button>

        {/* Backend + frontend versions at the bottom of the left menu (project rule) */}
        {!collapsed && (
          <div className="mt-2.5 pt-2 border-t" style={{ borderColor: 'var(--wfm-sidebar-border)' }}>
            <VersionFooter inverse />
          </div>
        )}
      </div>
    </div>
  );
}
